import { useMutation, useQueryClient } from '@tanstack/react-query';
import { endpoints } from '@rossum/api-client';
import { api } from '../../../../lib/apiClient';
import { neighborsKeys } from './queryKeys';
import { NeighborAnnotationMetadata } from './useNeighborAnnotationsMetadata';

type SetTrainingEnabledParams = {
  annotationId: number;
  enabled: boolean;
};

type MetadataQueryData = Map<number, NeighborAnnotationMetadata>;

const metadataQueryFilter = {
  queryKey: neighborsKeys.allAnnotationsMetadata(),
};

const withTrainingEnabled = (
  data: MetadataQueryData | undefined,
  { annotationId, enabled }: SetTrainingEnabledParams
) => {
  const metadata = data?.get(annotationId);
  if (!data || !metadata) return data;

  return new Map(data).set(annotationId, {
    ...metadata,
    trainingEnabled: enabled,
  });
};

/**
 * Toggles `training_enabled` on a single annotation. Optimistic, as the
 * decision is reversible from the same button.
 */
export const useSetNeighborTrainingEnabled = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (params: SetTrainingEnabledParams) => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ annotationId, enabled }: SetTrainingEnabledParams) =>
      api.request(
        endpoints.annotations.patch(annotationId, { trainingEnabled: enabled })
      ),
    onMutate: async params => {
      await queryClient.cancelQueries(metadataQueryFilter);
      const previous =
        queryClient.getQueriesData<MetadataQueryData>(metadataQueryFilter);
      queryClient.setQueriesData<MetadataQueryData | undefined>(
        metadataQueryFilter,
        data => withTrainingEnabled(data, params)
      );
      return { previous };
    },
    onError: (_error, _params, context) => {
      context?.previous.forEach(([queryKey, data]) =>
        queryClient.setQueryData(queryKey, data)
      );
      onError?.();
    },
    onSuccess: (_data, params) => onSuccess?.(params),
    onSettled: () => queryClient.invalidateQueries(metadataQueryFilter),
  });
};
